import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import PortalAbsenceCalendarCard from "../components/portal/PortalAbsenceCalendarCard";
import PortalAbsencesCard from "../components/portal/PortalAbsencesCard";
import PortalBookableOccurrencesCard from "../components/portal/PortalBookableOccurrencesCard";
import PortalOverdueBanner from "../components/portal/PortalOverdueBanner";
import PortalPageHeading from "../components/portal/PortalPageHeading";
import PortalScheduleList from "../components/portal/PortalScheduleList";
import PortalSlotRequestCard from "../components/portal/PortalSlotRequestCard";
import { usePortalLink } from "../features/portal/hooks/usePortalLink";
import { usePortalPage } from "../features/portal/hooks/usePortalPage";
import {
  selectPortalBalance,
  selectPortalBookableOccurrences,
  selectPortalOccurrences,
} from "../features/portal/portal-slice";
import {
  fetchBookableOccurrences,
  fetchPortalBalance,
  fetchPortalOccurrences,
} from "../features/portal/slice-extensions/portal-slice-extension";
import { ROUTES } from "../routes";

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

/** Next: the member's coming dates — what to book, what is booked, when they are away. */
const PortalHomePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { session, me, memberId, members, selectMember } = usePortalPage();
  const moneyLink = usePortalLink(ROUTES.PORTAL_MONEY);
  const occurrences = useAppSelector(selectPortalOccurrences(memberId ?? ""));
  const balance = useAppSelector(selectPortalBalance(memberId ?? ""));
  const bookable = useAppSelector(selectPortalBookableOccurrences(memberId ?? ""));

  const range = useMemo(() => {
    const monthAhead = new Date();
    monthAhead.setDate(monthAhead.getDate() + 30);
    return { from: toIsoDate(new Date()), to: toIsoDate(monthAhead) };
  }, []);

  useEffect(() => {
    if (memberId) {
      dispatch(fetchPortalOccurrences({ memberId, from: range.from, to: range.to }));
      // The balance feeds the overdue line and the cancellation policy of each contract.
      dispatch(fetchPortalBalance(memberId));
    }
  }, [memberId, range, dispatch]);

  if (!session) return null;

  const absences = me?.features.absences === true;

  return (
    <section className="mx-auto w-full max-w-3xl px-4 py-8">
      <PortalPageHeading
        title={t("portal.welcome", "Hi {{name}}", { name: session.firstName })}
        intro={t("portal.homeIntro")}
        members={members}
        memberId={memberId}
        onSelectMember={selectMember}
      />

      <PortalOverdueBanner balance={balance} moneyTo={moneyLink} />

      {memberId && <PortalBookableOccurrencesCard memberId={memberId} scheduleRange={range} />}
      {memberId && <PortalSlotRequestCard memberId={memberId} scheduleRange={range} />}

      <div className={absences ? "grid gap-8 md:grid-cols-2" : undefined}>
        <div>
          <h2 className="mb-3 text-lg font-semibold text-grey-20 dark:text-white">{t("portal.upcoming")}</h2>
          <PortalScheduleList
            occurrences={occurrences}
            memberId={memberId ?? undefined}
            bookable={bookable}
            contracts={balance?.contracts}
            onCancelled={() => {
              if (!memberId) return;
              dispatch(fetchPortalOccurrences({ memberId, from: range.from, to: range.to }));
              dispatch(fetchBookableOccurrences({ memberId }));
            }}
          />
        </div>
        {absences && memberId && (
          <div className="space-y-4">
            <PortalAbsencesCard memberId={memberId} />
            {me?.features.absenceCalendar && <PortalAbsenceCalendarCard />}
          </div>
        )}
      </div>
    </section>
  );
};

export default PortalHomePage;
